/**
 * WhitelabelSetupBanner — shown ONLY to country_operator + client_admin users
 * whose whitelabel record is missing essentials (company_name, logo_url,
 * subdomain_slug, primary_color). Prompts them to finish setup before their
 * branded URL is shared with prospects.
 *
 * Why force this: an incomplete whitelabel makes shared Sales Deck links and
 * branded emails default back to "Konnect" / "konnect.kaharian.com" — which
 * is what users have been complaining about. Catching the missing fields at
 * the top of every page is the cheapest possible reminder.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { AlertTriangle, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const REQUIRED = [
  { key: 'company_name', label: 'Company name' },
  { key: 'logo_url', label: 'Logo' },
  { key: 'primary_color', label: 'Primary color' },
  { key: 'subdomain_slug', label: 'Subdomain or custom domain' },
];

export const WhitelabelSetupBanner = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [missing, setMissing] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  // Eligible roles: country_operator + client_admin on Enterprise plan.
  // Super Admin is the PLATFORM OWNER — they don't subscribe and shouldn't
  // be nagged about "finishing brand setup" on their own platform homepage.
  // If a super_admin wants to configure the public marketing site, they go
  // directly to Settings → Whitelabel Management (no banner needed).
  const role = user?.role;
  const planLower = (user?.plan || '').toLowerCase();
  const eligible = role === 'country_operator'
    || (role === 'client_admin' && planLower === 'enterprise');

  useEffect(() => {
    if (!eligible) return;
    let cancelled = false;
    axios.get(`${API}/api/whitelabel/me`).then((r) => {
      if (cancelled) return;
      const cfg = r.data?.config || {};
      // For super_admin we additionally require custom_domain OR a verified
      // subdomain; for country_operator any of (subdomain_slug, custom_domain)
      // is enough.
      const hasDomain = !!(cfg.subdomain_slug || cfg.custom_domain);
      const miss = REQUIRED
        .filter((f) => {
          if (f.key === 'subdomain_slug') return !hasDomain;
          return !cfg[f.key];
        })
        .map((f) => f.label);
      setMissing(miss);
    }).catch(() => { /* silent — user just doesn't see banner */ });
    return () => { cancelled = true; };
  }, [eligible, user?.email]);

  // Don't show the banner ON the whitelabel page itself.
  if (!eligible || dismissed || missing.length === 0) return null;
  if (location.pathname.startsWith('/settings/my-whitelabel')) return null;

  return (
    <div
      className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-center gap-3"
      data-testid="whitelabel-setup-banner"
    >
      <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
      <div className="flex-1 text-sm">
        <strong className="text-amber-900">Finish your brand setup</strong>{' '}
        <span className="text-amber-800">
          — until you add {missing.join(', ')}, {role === 'client_admin'
            ? 'your customers will see Konnect branding on emails, proposals, and your portal instead of yours.'
            : 'prospects who open your shared links will see Konnect branding instead of yours.'}
        </span>
      </div>
      <Button asChild size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="whitelabel-setup-cta">
        <Link to="/settings/my-whitelabel">Complete brand setup →</Link>
      </Button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="text-amber-700/60 hover:text-amber-900 p-1"
        data-testid="whitelabel-setup-dismiss"
        title="Dismiss for this session"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default WhitelabelSetupBanner;
